'use client';

/**
 * ProductDocumentsCard — brochures + spec sheets + modular configs.
 *
 * Slices `FullProductDetail.documents` from the shared
 * `graph_product_explore` payload and renders via the existing
 * `<DocumentsSection />` primitive — so the canvas-scale paint and
 * rail-scale paint share the same role-grouped pill list, the same
 * `media_download` BFF strategy flow, and the same magic-byte
 * verification path for downloads.
 *
 * @see klyde_edge/ui_config/composition/cards/vendors.py — content_key="product_documents"
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas / overlay
 */

import { useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { useStrategyData } from '@/lib/api/hooks';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { DocumentsSection } from '@/components/copilot/rail-insights/sections/documents-section';
import { useCardExport } from '@/lib/design/card-export';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import type { ProductDocument } from '@/components/copilot/details/sections/types';
import type { ProductExploreResponse } from '@/lib/api/types';

const PRODUCT_EXPLORE_STRATEGY = 'graph_product_explore';

interface RawDocument {
  semantic_id?: string | null;
  name?: string | null;
  url?: string | null;
  type?: string | null;
  role?: string | null;
  mime_type?: string | null;
  sequence?: number | null;
}

export function ProductDocumentsCard() {
  const { entityId, entityType } = useViewportSelection();
  const enabled = !!entityId && entityType === 'product';

  const { data, isLoading } = useStrategyData<ProductExploreResponse>(
    PRODUCT_EXPLORE_STRATEGY,
    enabled ? { semantic_id: entityId } : {},
    enabled,
  );
  const product = data?.data?.product ?? null;

  // FullProductDetail.documents is typed loosely (the rail accepts
  // either a thin {name, url, type} or the richer MediaNode shape with
  // semantic_id + mime_type + role). Normalise to ProductDocument so
  // DocumentsSection's role-grouping and media_download path Just Work.
  const documents = useMemo<ProductDocument[]>(() => {
    const raw = (product?.documents ?? []) as RawDocument[];
    return raw.map((d, i) => ({
      semantic_id: d.semantic_id ?? null,
      name: d.name ?? 'Document',
      url: d.url ?? '',
      type: d.type ?? null,
      role: d.role ?? d.type ?? null,
      mime_type: d.mime_type ?? null,
      sequence: d.sequence ?? i,
    }));
  }, [product]);

  // Scene export — overlay (PDP) surface; the documents list.
  useCardExport(useMemo(() => ({
    label: 'Documents',
    content_key: 'product_documents',
    surface: 'overlay' as const,
    status: (product ? 'loaded' : enabled ? 'loading' : 'idle') as 'loading' | 'loaded' | 'idle',
    query_key: enabled
      ? ['strategy-data', PRODUCT_EXPLORE_STRATEGY, { semantic_id: entityId }]
      : null,
    text: documents.length ? documents.map((d) => d.name).join(', ') : 'Documents',
    json: {
      count: documents.length,
      documents: documents.map((d) => ({ name: d.name, type: d.type, role: d.role })),
    },
  }), [documents, product, enabled, entityId]));

  if (isLoading && !product) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground gap-2">
        <Loader2 className="h-5 w-5 animate-spin" />
        Loading…
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div className={cn(text.muted, 'h-full flex items-center justify-center')}>
        No documents on file.
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto">
      <DocumentsSection items={documents} label="Documents" />
    </div>
  );
}
